/**
 * Hack in support for Function.name for browsers that don't support it.
 * IE, I'm looking at you.
**/
if (Function.prototype.name === undefined && Object.defineProperty !== undefined) {
  Object.defineProperty(Function.prototype, 'name', {
      get: function() {
          var funcNameRegex = /function\s([^(]{1,})\(/;
          var results = (funcNameRegex).exec((this).toString());
          return (results && results.length > 1) ? results[1].trim() : "";
      },
      set: function(value) {}
  });
}

/*
 * Type definition section
 * 
 * We need those declarations because we want to resolve the parameter classes in constructors in compile time.
 * 
 * For example if we didn't have this, where there is `.asSingleton(ExampleClass2, [ExampleClass])`
 *    in code, we could easily mistyped as `.asSingleton(ExampleClass2, [ExampleClass2])` without a compilation warning.
 */
type ConstructorParamsLength<T extends new (...args: any) => any> = ConstructorParameters<T>['length'];
type ObjectWithConstructor = {new (...args: any): any};
type GenericConstructor = new (...args: any) => any;
type Constructor<T> = new (...args: any) => T;
type TypeOfConstructorParameters0<T extends GenericConstructor> = ConstructorParamsLength<T> extends 0 ? [] : never;
type TypeOfConstructorParameters1<T extends GenericConstructor> = T extends new (args0: infer P0) => any ? (ConstructorParamsLength<T> extends 1 ? [ Constructor<P0> ] : never) : never
type TypeOfConstructorParameters2<T extends GenericConstructor> = T extends new (args0: infer P0, args1: infer P1) => any ? (ConstructorParamsLength<T> extends 2 ? [ Constructor<P0>, Constructor<P1> ] : never) : never;
type TypeOfConstructorParameters3<T extends GenericConstructor> = T extends new (args0: infer P0, args1: infer P1, args2: infer P2) => any ? (ConstructorParamsLength<T> extends 3 ? [ Constructor<P0>, Constructor<P1>, Constructor<P2> ] : never) : never;
type TypeOfConstructorParameters4<T extends GenericConstructor> = T extends new (args0: infer P0, args1: infer P1, args2: infer P2, args3: infer P3) => any ? (ConstructorParamsLength<T> extends 4 ? [ Constructor<P0>, Constructor<P1>, Constructor<P2>, Constructor<P3> ] : never) : never;
type TypeOfConstructorParameters5<T extends GenericConstructor> = T extends new (args0: infer P0, args1: infer P1, args2: infer P2, args3: infer P3, args4: infer P4) => any ? (ConstructorParamsLength<T> extends 5 ? [ Constructor<P0>, Constructor<P1>, Constructor<P2>, Constructor<P3>, Constructor<P4> ] : never) : never;
type TypeOfConstructorParameters6<T extends GenericConstructor> = T extends new (args0: infer P0, args1: infer P1, args2: infer P2, args3: infer P3, args4: infer P4, args5: infer P5) => any ? (ConstructorParamsLength<T> extends 5 ? [ Constructor<P0>, Constructor<P1>, Constructor<P2>, Constructor<P3>, Constructor<P4>, Constructor<P5> ] : never) : never;
type TypeOfConstructorParameters7<T extends GenericConstructor> = T extends new (args0: infer P0, args1: infer P1, args2: infer P2, args3: infer P3, args4: infer P4, args5: infer P5, args6: infer P6) => any ? (ConstructorParamsLength<T> extends 7 ? [ Constructor<P0>, Constructor<P1>, Constructor<P2>, Constructor<P3>, Constructor<P4>, Constructor<P5>, Constructor<P6> ] : never) : never;
type TypeOfConstructorParameters8<T extends GenericConstructor> = T extends new (args0: infer P0, args1: infer P1, args2: infer P2, args3: infer P3, args4: infer P4, args5: infer P5, args6: infer P6, args7: infer P7) => any ? (ConstructorParamsLength<T> extends 8 ? [ Constructor<P0>, Constructor<P1>, Constructor<P2>, Constructor<P3>, Constructor<P4>, Constructor<P5>, Constructor<P6>, Constructor<P7> ] : never) : never;
type TypeOfConstructorParameters9<T extends GenericConstructor> = T extends new (args0: infer P0, args1: infer P1, args2: infer P2, args3: infer P3, args4: infer P4, args5: infer P5, args6: infer P6, args7: infer P7, args8: infer P8) => any ? (ConstructorParamsLength<T> extends 9 ? [ Constructor<P0>, Constructor<P1>, Constructor<P2>, Constructor<P3>, Constructor<P4>, Constructor<P5>, Constructor<P6>, Constructor<P7>, Constructor<P8> ] : never) : never;
type TypeOfConstructorParameters10<T extends GenericConstructor> = T extends new (args0: infer P0, args1: infer P1, args2: infer P2, args3: infer P3, args4: infer P4, args5: infer P5, args6: infer P6, args7: infer P7, args8: infer P8, args9: infer P9) => any ? (ConstructorParamsLength<T> extends 10 ? [ Constructor<P0>, Constructor<P1>, Constructor<P2>, Constructor<P3>, Constructor<P4>, Constructor<P5>, Constructor<P6>, Constructor<P7>, Constructor<P8>, Constructor<P9> ] : never) : never;

type TypeOfConstructorParameters<T extends GenericConstructor> =
  TypeOfConstructorParameters0<T>
  | TypeOfConstructorParameters1<T>
  | TypeOfConstructorParameters2<T>
  | TypeOfConstructorParameters3<T>
  | TypeOfConstructorParameters4<T>
  | TypeOfConstructorParameters5<T>
  | TypeOfConstructorParameters6<T>
  | TypeOfConstructorParameters7<T>
  | TypeOfConstructorParameters8<T>
  | TypeOfConstructorParameters9<T>
  | TypeOfConstructorParameters10<T>;

enum EntryType {
  Singleton,
  Transient,
}

interface IMetadata {
  [key: string]: IMetadataEntry<any>
}

interface IMetadataEntryBase {
  Type: EntryType;
  Name: string;
}

interface IMetadataEntry<T extends ObjectWithConstructor> extends IMetadataEntryBase {
  SingletonInstance: Object;
  ConstructorTypeArgs: TypeOfConstructorParameters<T>;
  ClassConstructor: Constructor<T>;
}

/**
 * Service container that allows registration and resolve of services in a type safe way.
 */
export class Container {
  private readonly metadata: IMetadata = {};

  /**
   * Registers an object as singleton
   * @param classConstructor The class that will be registered
   * @param object The instance
   */
  public asSingletonObject<T>(classConstructor: Constructor<T>, object: T): Container {
    this.registerObject(EntryType.Singleton, classConstructor, null, object);
    return this;
  }

  /**
   * Registers a class as a singleton. The instance will be created when the class is been requested
   * @param classConstructor The class that will be registered
   * @param constructorTypeArgs The constructor parameter classes as a tuple
   */
  public asSingleton<T extends ObjectWithConstructor>(classConstructor: T, constructorTypeArgs: TypeOfConstructorParameters<T>): Container {
    this.registerObject(EntryType.Singleton, classConstructor, constructorTypeArgs);
    return this;
  }

  /**
   * Registers a class as transient. When an object is requested, a new object tree will be created (excluding singleton objects)
   * @param classConstructor The class that will be registered
   * @param constructorTypeArgs The constructor parameter classes as a tuple
   */
  public asTransient<T extends ObjectWithConstructor>(classConstructor: T, constructorTypeArgs: TypeOfConstructorParameters<T>): Container {
    this.registerObject(EntryType.Transient, classConstructor, constructorTypeArgs);
    return this;
  }

  /**
   * Resolves an object tree and returns an instance for it
   * @param classConstructor The class that needs to be resolved
   * @returns An instance of the specified class
   * @throws Error when cannot resolve the item
   */
  public resolve<T>(classConstructor: Constructor<T>): T {
    if (!!this.metadata[classConstructor.name]) {
      return <T> this.retrieveObject(this.metadata[classConstructor.name]);
    }

    throw new Error(`Type not registered: ${classConstructor.name}`);
  }

  private registerObject<T extends ObjectWithConstructor>(type: EntryType, classConstructor: T, constructorTypeArgs: TypeOfConstructorParameters<T>, singletonInstance: Object = null) {
    const existingEntry = <IMetadataEntry<T>> this.metadata[classConstructor.name];
    
    const newEntry: IMetadataEntry<T> = {
      ClassConstructor: classConstructor,
      ConstructorTypeArgs: constructorTypeArgs || (existingEntry ? existingEntry.ConstructorTypeArgs : null),
      SingletonInstance: singletonInstance || (existingEntry ? existingEntry.SingletonInstance : null),
      Type: type,
      Name: classConstructor.name,
    };
    this.metadata[classConstructor.name] = newEntry;
  }

  private retrieveObject<T extends ObjectWithConstructor>(metadataEntry: IMetadataEntry<T>, resolveStack: IMetadataEntryBase[] = [], requestTypeScope: EntryType = metadataEntry.Type): Object {
    // Check if entry exists
    for(const existingEntry of resolveStack) {
      if (existingEntry.Name === metadataEntry.Name) {
        throw new Error(`Circular dependency: ${metadataEntry.Name} -> Stack: ${resolveStack.map(x => x.Name).reduce((x, y) => x + ' => ' + y)}`);
      }
    }

    // Check for scope consistency
    if (metadataEntry.Type === EntryType.Transient && requestTypeScope === EntryType.Singleton) {
      throw new Error(`Scope inconsistent (Requested Transient in Singleton): ${metadataEntry.Name} -> Stack: ${resolveStack.map(x => x.Name).reduce((x, y) => x + ' => ' + y)}`);
    }
    
    // Try to resolve the type tree
    resolveStack.push(metadataEntry);
    if (metadataEntry.Type === EntryType.Singleton && !!metadataEntry.SingletonInstance) {
      resolveStack.pop();
      return metadataEntry.SingletonInstance;
    }
    
    const constructorArgs: any[] = [];
    for(const item of metadataEntry.ConstructorTypeArgs) {
      const entry = this.metadata[item.name];
      if (!entry) {
        throw new Error(`Type not registered: ${item.name} -> Stack: ${resolveStack.map(x => x.Name).reduce((x, y) => x + ' => ' + y)}`);
      }

      const argObject = this.retrieveObject(entry, resolveStack, requestTypeScope);
      constructorArgs.push(argObject);
    }

    // Create the object
    const instance = new metadataEntry.ClassConstructor(...constructorArgs);

    if (metadataEntry.Type === EntryType.Singleton) {
      metadataEntry.SingletonInstance = instance;
    }

    // resolveStack.pop(); // We don't need that here since we are successful
    return instance;
  }
}


// WEBPACK FOOTER //
// ./src/Containers/Container.ts